// =============================================================================
// src/app/format-ptbr.ts — Formatadores numericos pt-BR (puros).
//
// Proveniencia: criado na reforma institucional 2026-06 (decisao D3): numeros
//   exibidos ao gabinete devem ser 100% pt-BR. Convencao do projeto: markup
//   ESTATICO mantem literais pt-BR escritos a mao; estes formatadores sao
//   OBRIGATORIOS para strings COMPUTADAS (contadores, sync de filtros) — e os
//   testes assertam VIA o proprio formatador (neutraliza variacao NBSP/ICU
//   entre versoes do Intl).
// =============================================================================

/** Intl pt-BR compartilhado para inteiros com separador de milhar (1.234.567). */
const MILHAR_PT = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });

/**
 * Inteiro com separador de milhar pt-BR.
 *
 * @example formatMilharPt(1242660) // '1.242.660'
 */
export function formatMilharPt(value: number): string {
  return MILHAR_PT.format(value);
}

/**
 * Percentual pt-BR com casas fixas (virgula decimal, sufixo %).
 *
 * @example formatPercentPt(54.2, 1) // '54,2%'
 */
export function formatPercentPt(value: number, decimals: number): string {
  return `${value.toFixed(decimals).replace('.', ',')}%`;
}

/**
 * Moeda compacta institucional: "R$ 4,2 bi" / "R$ 320 mi" / "R$ 6.420".
 * Bilhoes/milhoes com 1 casa quando nao inteiros; abaixo de 1 mi usa milhar.
 *
 * @example formatCurrencyBrlCompactPt(4_200_000_000) // 'R$ 4,2 bi'
 */
export function formatCurrencyBrlCompactPt(value: number): string {
  if (value >= 1_000_000_000) return `R$ ${compactUnitPt(value / 1_000_000_000)} bi`;
  if (value >= 1_000_000) return `R$ ${compactUnitPt(value / 1_000_000)} mi`;
  return `R$ ${formatMilharPt(value)}`;
}

/**
 * Pessoas em forma compacta: "84 mil" / "2,4 mi" (nada de "84k").
 *
 * @example formatPeopleCompactPt(84_000) // '84 mil'
 */
export function formatPeopleCompactPt(value: number): string {
  if (value >= 1_000_000) return `${compactUnitPt(value / 1_000_000)} mi`;
  if (value >= 1_000) return `${compactUnitPt(value / 1_000)} mil`;
  return formatMilharPt(value);
}

/**
 * Indice com sinal explicito e virgula decimal: "+0,34" / "−38".
 * Usa o sinal de menos tipografico (U+2212) do vocabulario da Crise/Reputacao.
 *
 * @example formatSignedIndexPt(0.34, 2) // '+0,34'
 */
export function formatSignedIndexPt(value: number, decimals: number): string {
  const absText = Math.abs(value).toFixed(decimals).replace('.', ',');
  return value < 0 ? `−${absText}` : `+${absText}`;
}

/** Unidade compacta: 1 casa decimal com virgula, sem casa quando inteiro. */
function compactUnitPt(unit: number): string {
  const rounded = Math.round(unit * 10) / 10;
  return Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toFixed(1).replace('.', ',');
}
